import { IconButton } from '@civilgrid/ui/components';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { deleteSingleRequestFile as apiDeleteSingleFile, getCivilSearchFileDownloadUrl } from '~/api/civilsearch';
import { downloadAndTrackFile } from '~/common/trackFileDownload';
import type { CivilGridFile } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { usePendingDownload } from '~/hooks/usePendingDownload';
import DeleteDialog from '~/pages/admin/civilsearch/common/DeleteDialog';
import store from '../../store';
import { columns as columnsBase } from '../supportingFiles/columns';
import FileViewer from '../supportingFiles/FileViewer';
import LinkRequest from './LinkRequest';
import evidenceStore from './store';

export const columns: ColumnDef<CivilGridFile>[] = [
  ...columnsBase.filter((c) => c.id !== 'actions'),
  {
    id: 'actions',
    header: 'Actions',
    size: 150,
    enableSorting: false,
    cell: (cell) => {
      const { project } = useSnapshot(store) as typeof store;
      const { projectRequests } = useSnapshot(evidenceStore) as typeof evidenceStore;
      const { pendingId, track } = usePendingDownload();

      const file = cell.row.original;
      const id = file.id;

      const onDelete = async () => {
        try {
          await apiDeleteSingleFile(project!.id, id);

          Notify.info({ message: 'File successfully deleted' });
          cell.table.options.meta?.refreshData?.();
        } catch {
          Notify.error({ message: 'Error deleting file' });
        }
      };

      // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
      const confirmation = useMemo(() => {
        const count = Object.keys(projectRequests).filter((rId) => {
          const req = projectRequests[rId];
          const f = req.files.find((_f) => _f.id === file.id);
          if (f) return true;
          return false;
        }).length;

        if (count > 1) {
          return (
            <div>
              This file is linked to <span className="font-bold">{count}</span> request(s). By deleting this file, it
              will be deleted for all <span className="font-bold">{count}</span> request(s). To remove this file from
              this request, click <span className="font-bold">Link Request</span> for this file.
            </div>
          );
        }

        return null;
      }, [projectRequests]);

      return (
        <div className="flex w-full items-center space-x-2">
          <DeleteDialog
            title="Delete File"
            trigger={
              <IconButton
                variant="hollow"
                icon="trash"
                aria-label={`Delete ${file.name}`}
                data-testid={`evidence-file-delete-${file.id}`}
              />
            }
            onConfirm={onDelete}
          >
            {confirmation}
          </DeleteDialog>

          <FileViewer file={file} />

          <IconButton
            variant="hollow"
            icon="download"
            aria-label={`Download ${file.name}`}
            data-testid={`evidence-file-download-${file.id}`}
            loading={pendingId === file.id}
            onClick={() =>
              track(file.id, () =>
                downloadAndTrackFile(file, { source: 'evidence', project_id: project?.id }, () =>
                  getCivilSearchFileDownloadUrl('utility-request-file', file.id, project?.id),
                ),
              )
            }
          />
        </div>
      );
    },
  },
  {
    id: 'link-request',
    header: 'Link Request',
    enableSorting: false,
    cell: ({ row, table }) => <LinkRequest file={row.original} parentTable={table} />,
  },
];
